// Tarjeta «Apartados ahora»: cada apartado vivo con su tienda, vendedor, hora y en qué va su venta.
import { Link } from '@tanstack/react-router';
import type { InventoryLiveReservation } from '@/api/services/retail-catalog/backoffice';
import { cn } from '@/lib/cn';
import { CellText, DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import { StatusPill } from '@/shared/patterns/Pills';
import { useReservationSessions } from '../../api/useReservationSessions';
import type { StoreDirectory } from '../../lib/inventoryDetail';
import {
    liveReservationRows,
    reservationSessionIds,
    reservationStateView,
    reservedSinceLabel,
    type LiveReservationRow,
} from '../../lib/inventoryReservations';
import { DetailCard } from './DetailCard';

const COLUMNS: DataTableColumn<LiveReservationRow>[] = [
    {
        key: 'store',
        label: 'Tienda',
        width: '28%',
        cell: (r) => (
            <span className="flex min-w-0 items-baseline gap-1">
                <span className="truncate font-semibold">{r.storeName}</span>
                <span className="flex-none font-mono text-[9.5px] text-muted-foreground">
                    {r.quantity > 1 ? `${r.storeCode} · ×${String(r.quantity)}` : r.storeCode}
                </span>
            </span>
        ),
        cellTitle: (r) => r.storeName,
    },
    {
        key: 'seller',
        label: 'Vendedor',
        width: '24%',
        cell: (r) => <CellText>{r.sellerName}</CellText>,
        cellTitle: (r) => r.sellerName,
    },
    {
        key: 'since',
        label: 'Desde',
        width: '12%',
        numeric: true,
        cell: (r) => reservedSinceLabel(r.reservedAt),
    },
    {
        key: 'state',
        label: 'Estado',
        width: '36%',
        cellOverflow: 'nowrap',
        cell: (r) => {
            const view = reservationStateView(r);
            return (
                <span className="flex items-center gap-1.5">
                    <StatusPill tone={view.tone}>{view.label}</StatusPill>
                    {r.saleId ? (
                        <Link
                            to="/retail-admin/$section"
                            params={{ section: 'ventas' }}
                            search={{ venta: r.saleId }}
                            className="text-[10.5px] font-semibold text-primary underline-offset-2 hover:underline"
                        >
                            Ver venta
                        </Link>
                    ) : null}
                </span>
            );
        },
    },
];

export function LiveReservationsCard({
    reservations,
    stores,
    storeId,
}: {
    /** `liveReservations` de la ficha; ausente o vacío oculta la tarjeta. */
    reservations: readonly InventoryLiveReservation[] | undefined;
    stores: StoreDirectory;
    storeId: string | undefined;
}) {
    const visible = (reservations ?? []).filter((r) => !storeId || r.storeId === storeId);
    const sessions = useReservationSessions(reservationSessionIds(visible));
    if (visible.length === 0) return null;
    const rows = liveReservationRows(visible, sessions.data ?? [], stores, storeId);
    return (
        <DetailCard
            title="Apartados ahora"
            aside={`${String(rows.reduce((sum, r) => sum + r.quantity, 0))} en curso`}
            flush
        >
            {sessions.error ? (
                <p className="border-b border-border/40 px-2.5 py-1.5 text-[10.5px] italic text-muted-foreground">
                    No se pudo leer el estado de las ventas: van sin vendedor ni estado.
                </p>
            ) : null}
            <DataTable
                caption="Apartados ahora"
                columns={COLUMNS}
                rows={rows}
                rowKey={(r) => r.reservationId}
                density="compact"
                rowClassName={(r) =>
                    cn(reservationStateView(r).highlight && 'bg-warning-soft hover:bg-warning-soft')
                }
                empty="Sin apartados en curso."
            />
        </DetailCard>
    );
}
